import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { authApi, type MeResponse, type UiTheme, type UserSummary } from "../api/auth";
import { applyTheme } from "../lib/theme";
import { NO_SCOPE, StructureScopeContext } from "../lib/structureScope";
import { resetSessionExpiredNotice, setSessionExpiredHandler } from "../api/client";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | {
      status: "authenticated";
      user: UserSummary;
      permissions: Set<string>;
      /** Branches the structure functions are narrowed to (sprint 7 §12); empty = everywhere. Read through `useStructureScope`. */
      structureScope: ReadonlySet<string>;
    };

type AuthContextValue = {
  state: AuthState;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Persists the caller's theme on the account and applies it right away. */
  setTheme: (theme: UiTheme) => Promise<void>;
  /** The only access decision the UI makes: an effective function key `module:action` (sprint 7). */
  hasPermission: (permission: string) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  // Mirror of state.status for the session-expired handler — it runs outside
  // React's render cycle and must see the CURRENT status, not a stale closure.
  const statusRef = useRef<AuthState["status"]>("loading");
  useEffect(() => { statusRef.current = state.status; }, [state.status]);

  // When a 401 survives a failed token refresh, the session is gone: flip to
  // anonymous so route guards redirect to /login instead of leaving dead
  // widgets polling 401s. Only acts when we believed we were logged in.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (statusRef.current !== "authenticated") return;
      // Flag for the login page so it can explain WHY the user landed there.
      try { sessionStorage.setItem("bkm.sessionExpired", "1"); } catch { /* ignore */ }
      setState({ status: "anonymous" });
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me: MeResponse = await authApi.me();
      resetSessionExpiredNotice();
      applyTheme(me.user.theme);
      setState({
        status: "authenticated",
        user: me.user,
        permissions: new Set(me.permissions),
        structureScope: new Set(me.structureScope ?? []),
      });
    } catch {
      setState({ status: "anonymous" });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string, rememberMe?: boolean) => {
      await authApi.login(email, password, rememberMe);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setState({ status: "anonymous" });
    }
  }, []);

  const setTheme = useCallback(async (theme: UiTheme) => {
    // Paint first — the request is a preference save, not a gate.
    applyTheme(theme);
    setState((s) => (s.status === "authenticated" ? { ...s, user: { ...s.user, theme } } : s));
    await authApi.setTheme(theme);
  }, []);

  const hasPermission = useCallback(
    (permission: string) =>
      state.status === "authenticated" && state.permissions.has(permission),
    [state],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ state, login, logout, refresh, setTheme, hasPermission }),
    [state, login, logout, refresh, setTheme, hasPermission],
  );

  const structureScope = state.status === "authenticated" ? state.structureScope : NO_SCOPE;
  return (
    <AuthContext.Provider value={value}>
      <StructureScopeContext.Provider value={structureScope}>{children}</StructureScopeContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
